import { useLanguage } from 'context/language.context';
import { StringKey } from 'dictionaries/string-key.enum';
import { cn } from 'lib/utils';
import { forwardRef, InputHTMLAttributes, ReactNode, useState } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  errorMessage?: StringKey | string;
  onValueChange?: (value: string | number | undefined) => void;
  startIcon?: ReactNode;
  label?: ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      className,
      type,
      errorMessage,
      onFocus,
      onBlur,
      onChange,
      onValueChange,
      value: propValue,
      defaultValue,
      ...props
    },
    ref,
  ) => {
    const { t } = useLanguage();
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      if (onFocus) onFocus(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      if (onBlur) onBlur(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const currentValue = e.target.value;
      if (onChange) {
        onChange(e);
      }

      if (type === 'number' && onValueChange) {
        let newValue: number | undefined;
        if (currentValue === '') {
          newValue = undefined;
        } else {
          const parsedValue = parseInt(currentValue, 10);
          newValue = !isNaN(parsedValue) && parsedValue >= 0 ? parsedValue : 0;
        }
        onValueChange(newValue);
      } else if (onValueChange && type !== 'number') {
        onValueChange(currentValue);
      }
    };

    const hasContent =
      propValue !== undefined
        ? propValue !== '' && propValue !== null
        : defaultValue !== undefined && defaultValue !== '' && defaultValue !== null;

    return (
      <div className="font-medium flex flex-1 flex-col gap-2">
        {label && (
          <label className="text-sm md:text-m" htmlFor={props.id}>
            {label}
          </label>
        )}
        <input
          {...props}
          ref={ref}
          defaultValue={defaultValue}
          value={propValue}
          className={cn(
            'flex w-full rounded-lg bg-transparent p-4 placeholder:text-gray-800 outline-none ring-1 ring-gray-800 ring-offset-background disabled:cursor-not-allowed disabled:opacity-50',
            errorMessage
              ? 'border border-error text-error ring-error'
              : isFocused || hasContent
                ? 'border border-black'
                : 'border border-gray-800',
            className,
          )}
          type={'text'}
          inputMode={type === 'number' ? 'numeric' : undefined}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
        />
        {errorMessage && (
          <p className="w-0 min-w-[calc(100%-32px)] text-error text-sm font-light mt-1 mx-4">
            {t(errorMessage as StringKey) ?? errorMessage}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
